import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import {
  LdsButton,
  LdsSearch,
  useToastContext,
} from "@elilillyco/ux-lds-react";
import Select from "react-select";

import ApprovedSpecialtiesTable from "../components/approvedSpecialties/ApprovedSpecialtiesTable";
import ExpirationDateTab from "../components/approvedSpecialties/ExpirationDateTab";
import CLoader from "../components/shared/CLoader";
import infoToastConfig from "../components/shared/Toast";

import {
  FETCH_FILTER_LIST,
  FETCH_APPROVED_SPECIALTIES,
} from "../store/approvedSpecialties";

import exportExcelSheet from "../utils/overview/exportExcelSheet";

import "../assets/scss/apprSpecialties/approvedSpecialties.scss";

const ApprovedSpec = () => {
  const [activeTab, setActiveTab] = useState("approved");
  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedIndication, setSelectedIndication] = useState("");
  const [selectedQuarter, setSelectedQuarter] = useState("");
  const [selectedThpc, setSelectedThpc] = useState("");
  const [selectedDsst, setSelectedDsst] = useState("");
  const [selectedMdpr, setSelectedMdpr] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [searchText, setSearchText] = useState("");
  const [firstRender, setFirstRender] = useState(true);
  const [visibleSpecialties, setVisibleSpecialties] = useState([]);

  const { addToast } = useToastContext();

  const getTableData = (searchValue) => {
    return {
      brand: selectedBrand === null ? "" : selectedBrand,
      indication: selectedIndication === null ? "" : selectedIndication,
      thpc: selectedThpc === null ? "" : selectedThpc,
      dsst: selectedDsst === null ? "" : selectedDsst,
      mdpr: selectedMdpr === null ? "" : selectedMdpr,
      quarter: selectedQuarter === null ? "" : selectedQuarter,
      department: selectedDepartment === null ? "" : selectedDepartment,
      searchKeyword: searchValue === undefined ? searchText : searchValue,
    };
  };

  const dispatch = useDispatch();
  const {
    filterBrand,
    filterIndication,
    filterQuarter,
    filterDepartment,
    loading,
    approvedSpecialties,
    filterThpc,
    filterDsst,
    filterMdpr,
  } = useSelector(({ approvedSpecialties }) => approvedSpecialties);

  const getOptions = (options) =>
    options.map((option) => ({ value: option, label: option }));

  const customSelectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '40px',
      borderColor: state.isFocused ? '#0071ce' : '#ccc',
      boxShadow: state.isFocused ? '0 0 0 1px #0071ce' : 'none',
      '&:hover': { borderColor: '#0071ce' },
    }),
    menu: (provided) => ({ ...provided, zIndex: 9999 }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0071ce' : state.isFocused ? '#e6f2ff' : 'white',
      color: state.isSelected ? 'white' : '#333',
      '&:active': { backgroundColor: '#0071ce' },
    }),
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    dispatch(FETCH_FILTER_LIST());
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    setFirstRender(false);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!firstRender) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
    }
  }, [selectedBrand, selectedIndication, selectedQuarter, selectedDepartment, selectedThpc, selectedDsst, selectedMdpr]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tempSpecialties = JSON.parse(JSON.stringify(approvedSpecialties));
    if (tempSpecialties.length) {
      tempSpecialties.forEach((specialty) => {
        if (specialty.department_field === "RETAIL") {
          specialty.department_field = "COMMERCIAL";
        }
      });
    }
    setVisibleSpecialties(tempSpecialties);
  }, [approvedSpecialties]);

  const submitSearch = () => {
    dispatch(FETCH_APPROVED_SPECIALTIES(getTableData()));
  };

  const clearAllFilters = () => {
    setSelectedBrand("");
    setSelectedIndication("");
    setSelectedQuarter("");
    setSelectedDepartment("");
    setSearchText("");
    setSelectedMdpr("");
    setSelectedDsst("");
    setSelectedThpc(""); 

    // fetching data here if only search field was set else fetching it in useEffect
    if (
      selectedBrand === "" &&
      selectedIndication === "" &&
      selectedQuarter === "" &&
      selectedDepartment === "" &&
      selectedThpc === "" &&
      selectedDsst === "" &&
      selectedMdpr === "" &&
      searchText !== ""
    ) {
      dispatch(FETCH_APPROVED_SPECIALTIES(getTableData("")));
    }
  };

  const convertDateFormat = (dateObj) => {
    return dateObj ? moment(dateObj).format("MMMM DD, YYYY") : "";
  };

  const exportData = () => {
    const dataToExport = [];

    if (!Array.isArray(visibleSpecialties)) {
      addToast(
        infoToastConfig("Failed to export the specialties data.", "error")
      );
      return;
    }

    if (!visibleSpecialties.length) {
      addToast(infoToastConfig("No specialty data to export.", "warning"));
      return;
    }

    visibleSpecialties.forEach((specialty) => {
      dataToExport.push({
        Title: specialty.list_title || "",
        TherapeuticArea: specialty.thpc_nm || "",
        DiseaseState: specialty.dsst_nm || "",
        Molecule: specialty.mdpr_nm || "",
        Brand: specialty.brand_nm || "",
        Indication: specialty.indication_nm || "",
        Quarter: specialty.list_quarter || "",
        Department: specialty.department_field || "",
        "Approved Date": convertDateFormat(specialty.updated_date),
      });
    });

    exportExcelSheet(dataToExport, "Approved Specialities");
  };

  return (
    <>
      {/* <section className="section"> */}
        <div className="approved-page">
          {/* LDS Tab Group - above header */}
          <div className="lds-card lds-tab-group approved-tab-group" aria-label="Approved specialities tabs">
            <div className="lds-tab-header align-left">
              <div className="tab-btn-container">
                <button
                  className={`lds-button-clear-style tab-btn focus-3px ${activeTab === "approved" ? "active" : ""}`}
                  onClick={() => setActiveTab("approved")}
                >
                  Approved Specialities
                </button>
                <button
                  className={`lds-button-clear-style tab-btn focus-3px ${activeTab === "expiration" ? "active" : ""}`}
                  onClick={() => setActiveTab("expiration")}
                >
                  Expiration Date
                </button>
              </div>
              <div className="lds-tab-drawer">
                <button className="lds-button-clear-style tab-btn tab-drawer-btn active focus-3px">
                  <span className="lds-tab-drawer-label">
                    {activeTab === "approved" ? "Approved Specialities" : "Expiration Date"}
                  </span>
                </button>
              </div>
            </div>
            <div className="lds-tab-container tab-padded">
              {/* Tab 1: Approved Specialities */}
              <div className={`lds-tab ${activeTab === "approved" ? "active" : ""}`} id="tab-approved">
                <div className="mt-3">
                  <div className="row">
                    <div className="col-9">
                      <h2 className="pl-4">Approved Specialities</h2>
                    </div>
                    <div className="col-3 export-button-col">
                      <LdsButton
                        classes={`button-icon-left mr-3 ${
                          loading ? "outline secondary" : "blue-btn"
                        }`}
                        icon="DownloadSimple"
                        iconPosition="before"
                        onClick={exportData}
                        disabled={loading ? "disabled" : ""}
                      >
                        Export
                      </LdsButton>
                    </div>
                  </div>
                </div>
                <div className="row">
                  <div className="col-6">
                    <LdsSearch
                      id={"searchSpecialty"}
                      onChange={setSearchText}
                      value={searchText}
                      onSubmit={submitSearch}
                    />
                  </div>
                  <div className="col-6">
                    <LdsButton onClick={clearAllFilters}>Clear All</LdsButton>
                  </div>
                </div>
                {filterBrand.length > 0 &&
                  filterDepartment.length > 0 &&
                  filterQuarter.length > 0 &&
                  filterIndication.length > 0 &&
                  filterThpc.length > 0 &&
                  filterDsst.length > 0 &&
                  filterMdpr.length > 0 && (
                    <>
                      <div className="row filter">
                        <div className="col-3">
                          <label htmlFor="select-product">Therapeutic Area</label>
                          <Select
                            inputId="select-product"
                            name="select-product"
                            placeholder="Select"
                            isClearable
                            value={selectedThpc ? { value: selectedThpc, label: selectedThpc } : null}
                            options={getOptions(filterThpc)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedThpc(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3">
                          <label htmlFor="select-indication">Disease State</label>
                          <Select
                            inputId="select-indication"
                            name="select-indication"
                            placeholder="Select"
                            isClearable
                            value={selectedDsst ? { value: selectedDsst, label: selectedDsst } : null}
                            options={getOptions(filterDsst)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedDsst(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3">
                          <label htmlFor="select-quarter">Molecule</label>
                          <Select
                            inputId="select-quarter"
                            name="select-quarter"
                            placeholder="Select"
                            isClearable
                            value={selectedMdpr ? { value: selectedMdpr, label: selectedMdpr } : null}
                            options={getOptions(filterMdpr)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedMdpr(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3">
                          <label htmlFor="select-indication-filter">Indication</label>
                          <Select
                            inputId="select-indication-filter"
                            name="select-indication-filter"
                            placeholder="Select"
                            isClearable
                            value={selectedIndication ? { value: selectedIndication, label: selectedIndication } : null}
                            options={getOptions(filterIndication)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedIndication(option ? option.value : "")}
                          />
                        </div>
                      </div>
                      <div className="row filter">
                        <div className="col-3">
                          <label htmlFor="select-brand">Brand</label>
                          <Select
                            inputId="select-brand"
                            name="select-brand"
                            placeholder="Select"
                            isClearable
                            value={selectedBrand ? { value: selectedBrand, label: selectedBrand } : null}
                            options={getOptions(filterBrand)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedBrand(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3">
                          <label htmlFor="select-quarter-filter">Quarter</label>
                          <Select
                            inputId="select-quarter-filter"
                            name="select-quarter-filter"
                            placeholder="Select"
                            isClearable
                            value={selectedQuarter ? { value: selectedQuarter, label: selectedQuarter } : null}
                            options={getOptions(filterQuarter)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedQuarter(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3">
                          <label htmlFor="select-department">Department</label>
                          <Select
                            inputId="select-department"
                            name="select-department"
                            placeholder="Select"
                            isClearable
                            value={selectedDepartment ? { value: selectedDepartment, label: selectedDepartment } : null}
                            options={getOptions(filterDepartment)}
                            styles={customSelectStyles}
                            onChange={(option) => setSelectedDepartment(option ? option.value : "")}
                          />
                        </div>
                        <div className="col-3"></div>
                      </div>
                    </>
                  )}
                {loading ? (
                  <CLoader size={90} />
                ) : (
                  <div className="apprSpecTable">
                    <ApprovedSpecialtiesTable
                      visibleSpecialties={visibleSpecialties}
                      setVisibleSpecialties={setVisibleSpecialties}
                      convertDateFormat={convertDateFormat}
                    />
                  </div>
                )}
              </div>

              {/* Tab 2: Expiration Date */}
              <div className={`lds-tab ${activeTab === "expiration" ? "active" : ""}`} id="tab-expiration">
                {activeTab === "expiration" && <ExpirationDateTab />}
              </div>
            </div>
          </div>
        </div>
      {/* </section> */}
    </>
  );
};

export default ApprovedSpec;
